import React, { useState, useEffect, useRef } from 'react';
import { Building2, Plus, Search, Edit, Trash2, Eye, Users, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { CardLoadingSpinner, InlineSpinner } from '../ui/LoadingSpinner';
import { superAdminAPI } from '../../services/api';
import tenantService from '../../services/tenantService';
import TenantModal from './TenantModal';

const TenantManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTenant, setExpandedTenant] = useState(null);
  const [tenantAdmins, setTenantAdmins] = useState({});
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedSubscription, setSelectedSubscription] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedTenants, setSelectedTenants] = useState(new Set());
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  // Refs for table scrolling
  const tableScrollRef = useRef(null);
  const tableRef = useRef(null);

  // Load tenants on component mount, pagination changes, and filter changes
  useEffect(() => {
    // For filter changes, reset to first page and debounce
    if (searchTerm || selectedStatus || selectedSubscription) {
      const timeoutId = setTimeout(() => {
        if (currentPage !== 0) {
          setCurrentPage(0); // This will trigger another useEffect call
        } else {
          loadTenants();
        }
      }, 300); // Debounce search

      return () => clearTimeout(timeoutId);
    } else {
      // For pagination changes or initial load, call immediately
      loadTenants();
    }
  }, [currentPage, pageSize, searchTerm, selectedStatus, selectedSubscription]);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        size: pageSize,
        sortBy: 'name',
        sortDir: 'asc'
      };

      // Add filters
      if (searchTerm) params.search = searchTerm;
      if (selectedStatus) params.status = selectedStatus.toUpperCase();
      if (selectedSubscription) params.subscriptionType = selectedSubscription;

      const response = await superAdminAPI.getAllTenants(params);
      const data = response.data;

      setTenants(data.content || []);
      setTotalElements(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
    } catch (error) {
      console.error('Error loading tenants:', error);
      setTenants([]);
      setTotalElements(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const loadTenantAdmins = async (tenantId) => {
    try {
      const admins = await tenantService.getTenantAdmins(tenantId);
      setTenantAdmins(prev => ({
        ...prev,
        [tenantId]: admins
      }));
    } catch (error) {
      console.error('Error loading tenant admins:', error);

      // Set empty array when API fails - no hardcoded fallback data
      setTenantAdmins(prev => ({
        ...prev,
        [tenantId]: []
      }));

      console.log('No tenant admins loaded - API error or no admins exist for this tenant');
    }
  };

  const toggleTenantExpansion = async (tenantId) => {
    if (expandedTenant === tenantId) {
      setExpandedTenant(null);
    } else {
      setExpandedTenant(tenantId);
      if (!tenantAdmins[tenantId]) {
        await loadTenantAdmins(tenantId);
      }
    }
  };

  // Action handlers
  const handleViewTenant = async (tenant) => {
    setSelectedTenant(tenant);
    setShowViewModal(true);
  };

  const handleEditTenant = async (tenant) => {
    setSelectedTenant(tenant);
    setShowEditModal(true);
  };

  const handleDeleteTenant = async (tenant) => {
    if (window.confirm(`Are you sure you want to delete tenant "${tenant.name}"?`)) {
      try {
        await superAdminAPI.deleteTenant(tenant.id);
        loadTenants(); // Reload the list
        // TODO: Add success toast
      } catch (error) {
        console.error('Error deleting tenant:', error);
        // TODO: Add error toast
      }
    }
  };

  const handleAddTenant = () => {
    setSelectedTenant(null);
    setShowAddModal(true);
  };

  const handleModalSave = () => {
    loadTenants(); // Reload the tenant list
  };

  // Bulk selection handlers
  const handleSelectTenant = (tenantId) => {
    const newSelected = new Set(selectedTenants);
    if (newSelected.has(tenantId)) {
      newSelected.delete(tenantId);
    } else {
      newSelected.add(tenantId);
    }
    setSelectedTenants(newSelected);
    setIsSelectAllChecked(newSelected.size === tenants.length && tenants.length > 0);
  };

  const handleSelectAll = () => {
    if (isSelectAllChecked) {
      setSelectedTenants(new Set());
      setIsSelectAllChecked(false);
    } else {
      const allTenantIds = new Set(tenants.map(tenant => tenant.id));
      setSelectedTenants(allTenantIds);
      setIsSelectAllChecked(true);
    }
  };

  const handleBulkDelete = () => {
    if (selectedTenants.size === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedTenants.size} selected tenant(s)? This action cannot be undone.`)) {
      confirmBulkDelete();
    }
  };

  const confirmBulkDelete = async () => {
    try {
      const tenantIds = Array.from(selectedTenants);

      // Delete each tenant individually (since we don't have bulk delete API)
      for (const tenantId of tenantIds) {
        await superAdminAPI.deleteTenant(tenantId);
      }

      setSelectedTenants(new Set());
      setIsSelectAllChecked(false);
      loadTenants(); // Refresh the list
      alert(`Successfully deleted ${tenantIds.length} tenant(s)!`);
    } catch (error) {
      console.error('Error deleting tenants:', error);
      alert('Failed to delete some tenants. Please try again.');
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0); // Reset to first page
  };





  const filteredTenants = tenants;

  const getStatusBadge = (status) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    if (status === 'Active') {
      return `${baseClasses} bg-green-100 text-green-800`;
    }
    return `${baseClasses} bg-red-100 text-red-800`;
  };

  const getSubscriptionBadge = (subscription) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (subscription) {
      case 'Enterprise':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      case 'Premium':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'Basic':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  return (
    <div>
      <div className="page-header-compact">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold azure-text mb-1">Tenant Management</h1>
            <p className="text-sm text-blue-700 font-medium">
              Manage all institutional tenants and their administrators
            </p>
          </div>
          <div className="flex space-x-3">
            {selectedTenants.size > 0 && (
              <button
                onClick={handleBulkDelete}
                className="btn-danger flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedTenants.size})
              </button>
            )}
            <button onClick={handleAddTenant} className="btn-primary flex items-center">
              <Plus className="h-4 w-4 mr-2" />
              Add Tenant
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card mb-4 py-3 px-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search tenants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <select
            className="input-field w-auto"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            className="input-field w-auto"
            value={selectedSubscription}
            onChange={(e) => setSelectedSubscription(e.target.value)}
          >
            <option value="">All Subscriptions</option>
            <option value="BASIC">Basic</option>
            <option value="PREMIUM">Premium</option>
            <option value="ENTERPRISE">Enterprise</option>
          </select>
        </div>
      </div>

      {/* Tenants Table */}
      <div className="card">
        {loading ? (
          <CardLoadingSpinner message="Loading tenants..." />
        ) : (
          <div>
            {/* Table Container */}
            <div
              ref={tableScrollRef}
              className="table-container-fixed"
            >
              <div ref={tableRef}>
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="table-header w-10 sticky left-0 bg-slate-50 shadow-lg z-10">
                        <input
                          type="checkbox"
                          checked={isSelectAllChecked}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="table-header w-28 sticky left-10 bg-slate-50 shadow-lg z-10">Actions</th>
                      <th className="table-header">Tenant</th>
                      <th className="table-header hidden sm:table-cell">Code</th>
                      <th className="table-header hidden md:table-cell">Domain</th>
                      <th className="table-header hidden lg:table-cell">Status</th>
                      <th className="table-header hidden lg:table-cell">Subscription</th>
                      <th className="table-header hidden xl:table-cell">Students</th>
                      <th className="table-header hidden xl:table-cell">Admins</th>
                      <th className="table-header hidden xl:table-cell">Created</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {filteredTenants.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="table-cell text-center py-12">
                          <div className="text-slate-500">
                            {tenants.length === 0 ? 'No tenants found. Add some tenants to get started.' : 'No tenants match your search criteria.'}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredTenants.map((tenant) => (
                        <React.Fragment key={tenant.id}>
                          <tr className="hover:bg-slate-50">
                            <td className="table-cell sticky left-0 bg-white shadow-lg z-10">
                              <input
                                type="checkbox"
                                checked={selectedTenants.has(tenant.id)}
                                onChange={() => handleSelectTenant(tenant.id)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>
                            <td className="table-cell sticky left-10 bg-white shadow-lg z-10">
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => handleViewTenant(tenant)}
                                  className="text-slate-600 hover:text-slate-900 p-1 hover:bg-slate-100 transition-colors duration-200"
                                  title="View Tenant"
                                >
                                  <Eye className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={() => handleEditTenant(tenant)}
                                  className="text-slate-600 hover:text-slate-900 p-1 hover:bg-slate-100 transition-colors duration-200"
                                  title="Edit Tenant"
                                >
                                  <Edit className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={() => handleDeleteTenant(tenant)}
                                  className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 transition-colors duration-200"
                                  title="Delete Tenant"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            </td>
                            <td className="table-cell">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-6 w-6">
                        <div className="h-6 w-6 rounded-full bg-gradient-to-r from-cyan-100 to-teal-100 flex items-center justify-center shadow-sm">
                          <Building2 className="h-3 w-3 text-cyan-600" />
                        </div>
                      </div>
                      <div className="ml-2">
                        <div className="text-sm font-medium text-slate-900">
                          {tenant.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100 hidden sm:table-cell">
                    <span className="text-sm text-gray-900 font-mono">
                      {tenant.code}
                    </span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100 hidden md:table-cell">
                    <span className="text-sm text-gray-900">
                      {tenant.domain}
                    </span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100 hidden lg:table-cell">
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium ${
                      tenant.status === 'ACTIVE'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {tenant.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100 hidden lg:table-cell">
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium ${
                      tenant.subscriptionType === 'ENTERPRISE'
                        ? 'bg-purple-100 text-purple-800'
                        : tenant.subscriptionType === 'PREMIUM'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {tenant.subscriptionType}
                    </span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100 hidden xl:table-cell">
                    <span className="text-sm text-gray-900">
                      {(tenant.settings?.studentCount || tenant.studentCount || 0).toLocaleString()}
                    </span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100 hidden xl:table-cell">
                    <button
                      onClick={() => toggleTenantExpansion(tenant.id)}
                      className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800"
                      data-testid="expand-admins"
                    >
                      <span>{tenant.settings?.adminCount || tenant.adminCount || 0}</span>
                      {expandedTenant === tenant.id ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100 hidden xl:table-cell">
                              <span className="text-sm text-gray-900">
                                {new Date(tenant.createdAt).toLocaleDateString()}
                              </span>
                            </td>
                          </tr>

                          {/* Admin expansion row */}
                          {expandedTenant === tenant.id && (
                            <tr className="bg-slate-50">
                              <td colSpan="10" className="px-6 py-4">
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-slate-900 flex items-center">
                          <Users className="h-4 w-4 mr-2" />
                          Tenant Administrators
                        </h4>
                        {tenantAdmins[tenant.id] !== undefined ? (
                          tenantAdmins[tenant.id].length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {tenantAdmins[tenant.id].map((admin) => (
                                <div key={admin.id} className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-slate-900 truncate">{admin.name}</p>
                                      <p className="text-xs text-slate-600 truncate">{admin.email}</p>
                                      <p className="text-xs text-slate-500 mt-1">
                                        Last login: {admin.lastLogin ? new Date(admin.lastLogin).toLocaleDateString() : 'Never'}
                                      </p>
                                    </div>
                                    <div className="flex-shrink-0 ml-2">
                                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        admin.enabled === true || admin.status === 'ACTIVE'
                                          ? 'bg-green-100 text-green-800 border border-green-200'
                                          : 'bg-red-100 text-red-800 border border-red-200'
                                      }`}>
                                        {admin.enabled === true || admin.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              <div className="text-slate-500">
                                <Users className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                                <p className="text-sm">No administrators found for this tenant.</p>
                                <p className="text-xs text-slate-400 mt-1">Administrators can be added through the Admin Management section.</p>
                              </div>
                            </div>
                          )
                        ) : (
                          <div className="text-center py-4">
                            <InlineSpinner size="md" />
                            <p className="text-sm text-slate-600 mt-2">Loading administrators...</p>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="px-4 sm:px-6 py-4 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <span className="text-sm text-slate-700 text-center sm:text-left">
                  Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} tenants
                </span>
                <div className="flex items-center justify-center sm:justify-start space-x-2">
                  <label className="text-sm text-slate-700">Show:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                    className="input-field py-1 text-sm"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                  className="btn-secondary flex items-center disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </button>

                <span className="text-sm text-slate-700">
                  Page {currentPage + 1} of {totalPages}
                </span>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="btn-secondary flex items-center disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">Next</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <TenantModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        mode="add"
        onSave={handleModalSave}
      />

      <TenantModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        tenant={selectedTenant}
        mode="edit"
        onSave={handleModalSave}
      />

      <TenantModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        tenant={selectedTenant}
        mode="view"
      />
    </div>
  );
};

export default TenantManagement;
